<template>
  <div
    :class="classNames"
    @pointerenter="handlePointerEnter"
    @pointerleave="handlePointerLeave"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
  >
    <div class="bitts-toast__content">
      <FontAwesomeIcon
        v-if="!hideIcon"
        :icon="iconType"
        :style="{ height: '16px', width: '16px' }"
        class="bitts-toast__icon"
        aria-hidden="true"
      />
      <div class="bitts-toast__title">
        <span class="sr-only">
          {{ accessibilityLabel }}
        </span>
        <span class="bitts-toast__message" v-html="message" />
        <div
          v-if="description"
          class="bitts-toast__description"
          :class="hideIcon && 'ml-0'"
        >
          <span v-html="description" />
          <span v-if="$slots.link" class="ml-2">
            <slot name="link" />
          </span>
        </div>
      </div>
      <div class="ml-auto flex items-center">
        <BittsButton
          v-if="action"
          :text="action.text"
          type="white"
          size="x-small"
          variant="outline"
          class="mr-8"
          @click="action.onClick"
        />
        <BittsButton
          aria-label="Dismiss"
          variant="ghost"
          type="white"
          size="x-small"
          :center-icon="['fak', 'x']"
          @click="dismiss"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { capitalize } from 'lodash';
import { computed } from 'vue';

import BittsButton from '../BittsButton/BittsButton.vue';

import { ToastType } from './types';
import { useToastTimer } from './useToastTimer';

type MessageObjectAction = {
  onClick?: () => void;
  text?: string;
};
interface Props {
  action?: MessageObjectAction | null;
  description?: string;
  duration?: number;
  hideIcon?: boolean;
  message?: string;
  type?: ToastType;
}
const props = withDefaults(defineProps<Props>(), {
  action: null,
  description: '',
  duration: undefined,
  hideIcon: false,
  message: '',
  type: 'success',
});
const emit = defineEmits(['dismiss-toast']);

const isError = computed(() => props.type === 'error');
const accessibilityLabel = computed(() => `${capitalize(props.type)} message`);
const classNames = computed(() => {
  return ['bitts-toast', `bitts-toast-${props.type}`];
});
const iconType = computed(() => {
  return {
    error: ['fak', 'exclamation-circle'],
    success: ['fak', 'check-circle'],
    info: ['fak', 'information-circle'],
    warning: ['fak', 'warning'],
  }[props.type];
});

const DEFAULT_DURATION_SECONDS = 3.5;

const durationSeconds = computed(
  () => props.duration ?? DEFAULT_DURATION_SECONDS,
);
const durationMs = computed(() => durationSeconds.value * 1000);
const shouldPersist = computed(
  () =>
    isError.value ||
    durationSeconds.value <= 0 ||
    !Number.isFinite(durationSeconds.value),
);

const {
  handlePointerEnter,
  handlePointerLeave,
  handleFocusIn,
  handleFocusOut,
  dismiss,
} = useToastTimer({
  durationMs: () => durationMs.value,
  shouldPersist: () => shouldPersist.value,
  onDismiss: () => emit('dismiss-toast'),
});
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-toast {
  @apply mb-8 bg-neutral-background-strong/80 rounded-8 w-full md:max-w-[400px] shadow-overlay;
  .bitts-toast__icon {
    @apply text-white opacity-50;
  }
  .bitts-toast__content {
    @apply px-8 py-4 flex items-center gap-8;
  }
  .bitts-toast__title {
    @apply flex flex-col items-start;
  }
  .bitts-toast__message {
    @apply grow text-sm text-white font-bold;
  }
  .bitts-toast__description {
    @apply text-white text-sm;
  }
}
</style>
